import { jsxs, jsx } from "react/jsx-runtime";
import { forwardRef, useState, useRef } from "react";
import { IconShow, IconHide, IconClear } from "@bynder/icons";
import { InputWrapper, LeftItems, IconLeftContainer, PrefixContainer, InputStyled, RightItems, SuffixContainer, ClearButton, IconRightContainer } from "./Input.styles.js";
import { InteractiveIcon } from "./InteractiveIcon.js";
import { useTranslate } from "./useTranslate.js";
const Input = forwardRef(({
  hasClearButton = false,
  icon,
  iconRight,
  id,
  name,
  isDisabled = false,
  isReadOnly = false,
  isInvalid = false,
  isSmall = false,
  onBlur,
  onChange,
  onFocus,
  onKeyDown,
  placeholder,
  type = "text",
  value,
  variant = "outlined",
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  "aria-describedby": ariaDescribedBy,
  "aria-haspopup": ariaHasPopup,
  "aria-expanded": ariaExpanded,
  "aria-controls": ariaControls,
  "aria-autocomplete": ariaAutocomplete,
  "aria-activedescendant": ariaActiveDescendant,
  inputRef: inputRefProp,
  maxLength,
  prefix,
  suffix,
  autoFocus = false,
  role,
  autoComplete = "off",
  ...props
}, ref) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const internalInputRef = useRef(null);
  const inputRef = inputRefProp || internalInputRef;
  const {
    translate
  } = useTranslate();
  function handleOnChange(e) {
    const newValue = e.currentTarget.value;
    onChange == null ? void 0 : onChange(newValue);
  }
  const isClearButtonVisible = hasClearButton && value.length > 0 && type !== "number" && type !== "password";
  const passwordInputType = isPasswordVisible ? "text" : "password";
  const inputType = type === "password" ? passwordInputType : type;
  function focusInput() {
    var _a;
    (_a = inputRef == null ? void 0 : inputRef.current) == null ? void 0 : _a.focus();
  }
  function clearInput() {
    onChange == null ? void 0 : onChange("");
    focusInput();
  }
  function togglePasswordVisibility() {
    setIsPasswordVisible((prevState) => !prevState);
    focusInput();
  }
  const isPasswordType = type === "password";
  return /* @__PURE__ */ jsxs(InputWrapper, {
    ...props,
    $isDisabled: isDisabled,
    $isInvalid: isInvalid,
    $isSmall: isSmall,
    onClick: focusInput,
    ref,
    $variant: variant,
    children: [/* @__PURE__ */ jsxs(LeftItems, {
      "data-testid": "ds-input-left-items",
      children: [icon && /* @__PURE__ */ jsx(IconLeftContainer, {
        children: icon
      }), prefix && /* @__PURE__ */ jsx(PrefixContainer, {
        $isDisabled: isDisabled,
        children: prefix
      })]
    }), /* @__PURE__ */ jsx(InputStyled, {
      autoComplete,
      autoCorrect: "off",
      disabled: isDisabled,
      $isDisabled: isDisabled,
      readOnly: isReadOnly,
      onBlur,
      onChange: handleOnChange,
      onFocus,
      onKeyDown,
      placeholder,
      ref: inputRef,
      spellCheck: false,
      type: inputType,
      value,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      "aria-describedby": ariaDescribedBy,
      "aria-haspopup": ariaHasPopup,
      "aria-expanded": ariaExpanded,
      "aria-controls": ariaControls,
      "aria-autocomplete": ariaAutocomplete,
      "aria-activedescendant": ariaActiveDescendant,
      "aria-invalid": isInvalid,
      role,
      id,
      name,
      maxLength,
      autoFocus
    }), /* @__PURE__ */ jsxs(RightItems, {
      "data-testid": "ds-input-right-items",
      children: [suffix && /* @__PURE__ */ jsx(SuffixContainer, {
        $isDisabled: isDisabled,
        children: suffix
      }), isPasswordType && !isDisabled && /* @__PURE__ */ jsx(InteractiveIcon, {
        "data-testid": "ds-password-visibility-toggle",
        onClick: () => togglePasswordVisibility(),
        label: isPasswordVisible ? translate("input.hide-password") : translate("input.show-password"),
        icon: isPasswordVisible ? /* @__PURE__ */ jsx(IconShow, {}) : /* @__PURE__ */ jsx(IconHide, {}),
        isTooltipShown: true
      }), isClearButtonVisible && !isDisabled && /* @__PURE__ */ jsx(ClearButton, {
        onClick: clearInput,
        type: "button",
        "aria-label": translate("input.clear"),
        children: /* @__PURE__ */ jsx(IconClear, {})
      }), iconRight && /* @__PURE__ */ jsx(IconRightContainer, {
        children: iconRight
      })]
    })]
  });
});
export {
  Input
};
//# sourceMappingURL=Input.js.map
